/**
 * Progressive 50/50 Standard — Style-specific sections for GridEngine.
 *
 * hero_section: Partner name + title + subtitle
 * live_draw: Large jackpot display with 50/50 split, stats grid, countdown, CTA
 */
import React from 'react'
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import { useContent } from '../../../contexts/ContentContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'

/**
 * Progressive Standard Hero Section
 */
export function ProgressiveStandardHeroSection({ partner, t, c, editMode, handleTranslationUpdate }) {
  const { c: useC } = useContent()
  const ct = c || useC

  return (
    <>
      <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-2">
        {partner?.partner_name ? (
          `${partner.partner_name} Progressive 50/50`
        ) : (
          <InlineTranslationEdit
            translationKey="progressive.title"
            value={ct('progressive.title', t?.('progressive.title'))}
            fallback="Progressive 50/50"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        )}
      </h1>
      <p className="text-gray-600 dark:text-gray-400">
        <InlineTranslationEdit
          translationKey="progressive.subtitle"
          value={ct('progressive.subtitle', t?.('progressive.subtitle'))}
          fallback="The jackpot grows until someone wins!"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </p>
    </>
  )
}

/**
 * Progressive Standard Live Draw Section
 */
export function ProgressiveStandardLiveDrawSection({
  mainDraw,
  t,
  c,
  editMode,
  handleTranslationUpdate,
  onLoginClick,
  timeLeft,
  shouldAnimate,
  countdownStyle,
}) {
  const { c: useC } = useContent()
  const ct = c || useC
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)

  const jackpotAmount = Math.floor(mainDraw?.current_jackpot || 15000)
  const charityAmount = jackpotAmount
  const ticketsSold = mainDraw?.total_tickets_sold

  const drawDateLabel = mainDraw?.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : ''

  return (
    <>
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-2 text-sm font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide mb-2">
          <TrendingUp className="w-4 h-4" />
          <InlineTranslationEdit
            translationKey="progressive.growingJackpot"
            value={ct('progressive.growingJackpot', t?.('progressive.growingJackpot'))}
            fallback="Growing Jackpot"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </div>
        <div className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 dark:text-white mb-2">
          ${jackpotAmount.toLocaleString()}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          <InlineTranslationEdit
            translationKey="progressive.andGrowing"
            value={ct('progressive.andGrowing', t?.('progressive.andGrowing'))}
            fallback="and growing with every ticket sold!"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </p>
      </div>

      {/* 50/50 Split Visualization */}
      <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-6">
        <div className="text-center mb-3">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            <InlineTranslationEdit
              translationKey="progressive.splitTitle"
              value={ct('progressive.splitTitle', t?.('progressive.splitTitle'))}
              fallback="50% to Winner / 50% to Charity"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
            />
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex-1 bg-blue-500 rounded-lg p-4 text-center text-white">
            <div className="text-xs uppercase tracking-wide mb-1">{ct('progressive.winner', t?.('progressive.winner', 'Winner'))}</div>
            <div className="text-xl md:text-2xl font-bold">${(jackpotAmount / 2).toLocaleString()}</div>
          </div>
          <div className="flex-1 bg-green-500 rounded-lg p-4 text-center text-white">
            <div className="text-xs uppercase tracking-wide mb-1">{ct('progressive.charity', t?.('progressive.charity', 'Charity'))}</div>
            <div className="text-xl md:text-2xl font-bold">${(charityAmount / 2).toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        {ticketsSold != null && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs uppercase tracking-wide">{ct('classic5050.ticketsSold', t?.('classic5050.ticketsSold', 'Tickets Sold'))}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {ticketsSold.toLocaleString()}
            </div>
          </div>
        )}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 mb-1">
            <Calendar className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wide">{ct('progressive.nextDraw', t?.('progressive.nextDraw', 'Next Draw'))}</span>
          </div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">
            {drawDateLabel || ct('progressive.tba', t?.('progressive.tba', 'TBA'))}
          </div>
        </div>
        <div className="col-span-2 md:col-span-1 bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wide">{ct('progressive.timeLeft', t?.('progressive.timeLeft', 'Time Left'))}</span>
          </div>
          <CountdownTimerStyles
            timeLeft={timeLeft}
            shouldAnimate={shouldAnimate}
            style={countdownStyle}
          />
        </div>
      </div>

      {/* Buy Tickets CTA */}
      <div className="text-center">
        <button
          onClick={() => purchaseNavigate('/purchase')}
          className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-bold text-lg rounded-lg shadow-lg transition-colors"
        >
          <Ticket className="w-5 h-5" />
          <InlineTranslationEdit
            translationKey="progressive.buyTickets"
            value={ct('progressive.buyTickets', t?.('progressive.buyTickets'))}
            fallback="Buy Tickets"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </>
  )
}
